import { REALMS } from '../../../../shared/src/realms';
import { realmTarget, type CoverageTarget } from '../../../../shared/src/taxonomyV2';
import { poolGaps, type RealmGap, type TopicGap } from './CoverageTargets';
import type { RealmStats } from './AdminContext';

/**
 * The whole pool at one zoom level: ten realms, twenty-two topics each, one
 * tile per topic.
 *
 * Tiles are coloured by the gap against the SELECTED TARGET, not by raw count.
 * That is the difference between an inventory and a plan: a topic holding 24
 * questions is finished at the launch floor and empty at deep coverage, so the
 * map has to repaint when the target changes. A map of raw counts cannot tell
 * you what to do next.
 *
 * One component, mounted both inside the factory (as its map view) and on the
 * Coverage page, so the two can never disagree about what "empty" means. The
 * previous Coverage page read the retired v1 taxonomy and showed hundreds of
 * zeroes for topics nobody generates to any more.
 */

function tileColors(t: TopicGap): { bg: string; border: string; fg: string } {
  if (t.have === 0) return { bg: '#2a0f16', border: '#f43f5e66', fg: '#f8a3b4' };
  if (t.gap === 0) return { bg: '#0c1f1b', border: '#14b8a666', fg: '#7fd8c8' };
  const pct = t.have / t.target;
  return pct >= 0.5
    ? { bg: '#231a0d', border: '#c4973a55', fg: '#d8b877' }
    : { bg: '#2a1a0c', border: '#e8873a55', fg: '#e0a878' };
}

interface Props {
  stats: Record<string, RealmStats>;
  target: CoverageTarget;
  /** Highlighted realm, when the grid sits next to a realm view. */
  activeRealm?: string;
  onPickRealm?: (realm: string) => void;
  onPickTopic?: (realm: string, topic: TopicGap) => void;
  compact?: boolean;
}

export function CoverageGrid({ stats, target, activeRealm, onPickRealm, onPickTopic, compact }: Props) {
  const gaps: RealmGap[] = poolGaps(stats, target);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 14 : 20 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.82rem', color: '#bebec6' }}>
        <span><i style={{ display: 'inline-block', width: 9, height: 9, background: '#2a0f16', border: '1px solid #f43f5e66', marginRight: 5 }} />hic soru yok</span>
        <span><i style={{ display: 'inline-block', width: 9, height: 9, background: '#2a1a0c', border: '1px solid #e8873a55', marginRight: 5 }} />hedefin yarisindan az</span>
        <span><i style={{ display: 'inline-block', width: 9, height: 9, background: '#231a0d', border: '1px solid #c4973a55', marginRight: 5 }} />hedefe yakin</span>
        <span><i style={{ display: 'inline-block', width: 9, height: 9, background: '#0c1f1b', border: '1px solid #14b8a666', marginRight: 5 }} />hedef tamam</span>
        <span style={{ marginLeft: 'auto', color: '#808080' }}>karoya tikla, o konunun eksigini uret</span>
      </div>

      {[...gaps].sort((a, b) => b.gap - a.gap).map(g => {
        const color = (REALMS as any)[g.realm]?.color ?? '#c4973a';
        const active = g.realm === activeRealm;
        return (
          <div
            key={g.realm}
            style={{
              border: `1px solid ${active ? color : '#161616'}`,
              borderRadius: 8,
              padding: compact ? '10px 12px' : '14px 16px',
              background: active ? '#0e0c12' : '#0a0a0a',
            }}
          >
            <div
              onClick={() => onPickRealm?.(g.realm)}
              style={{
                display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10,
                cursor: onPickRealm ? 'pointer' : 'default',
              }}
            >
              <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.9rem', color }}>
                {g.realm}
              </span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: '#c6c6ce' }}>
                {g.have} / {realmTarget(target, g.realm)}
              </span>
              <span style={{ marginLeft: 'auto', fontSize: '0.85rem', fontWeight: 600, color: g.gap ? '#c4973a' : '#14b8a6' }}>
                {g.gap ? `${g.gap} eksik` : 'tamam'}
              </span>
              {g.empty > 0 && (
                <span style={{ fontSize: '0.85rem', color: '#f43f5e', fontWeight: 600 }}>{g.empty} bos</span>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {[...g.topics].sort((a, b) => b.gap - a.gap).map(t => {
                const c = tileColors(t);
                return (
                  <button
                    key={t.topic}
                    onClick={() => onPickTopic?.(g.realm, t)}
                    title={`${t.topic}\n${t.have} var, hedef ${t.target}\neksik E/M/H: ${t.byDiff.join(' / ')}`}
                    style={{
                      background: c.bg, border: `1px solid ${c.border}`, color: c.fg,
                      borderRadius: 5, padding: '5px 8px', cursor: onPickTopic ? 'pointer' : 'default',
                      fontSize: '0.8rem', lineHeight: 1.3, textAlign: 'left',
                      maxWidth: compact ? 168 : 210,
                    }}
                  >
                    <div style={{
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: compact ? 150 : 192,
                    }}>
                      {t.topic}
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', opacity: 0.8, marginTop: 2 }}>
                      {t.have}
                      {t.gap > 0 && <span style={{ opacity: 0.75 }}> · {t.gap} eksik</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
